// src/components/news/NewsTab.js

import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import api from '../../api';
import { useNewsContext } from '../../context/NewsContext';
import NewsArticleCard from './NewsArticleCard';

import piNetworkLogo from '../../assets/pinetworklogo-icon.jpg';

import './NewsTab.css';

const PAGE_LIMIT = 10;
const SEARCH_DEBOUNCE_MS = 350;


function NewsTab({ query = '' }) {
  /*
   * The normal Latest News list comes from
   * NewsContext and remains cached when this
   * component unmounts.
   */
  const {
    articles: cachedArticles,
    pagination: cachedPagination,
    loading: cachedLoading,
    hasFetched: cachedHasFetched,
    error: cachedError,
    loadingMore: cachedLoadingMore,
    ensureNews,
    refreshNews,
    loadMoreNews,
  } = useNewsContext();

  /*
   * Search results remain local because they
   * must never overwrite the Latest News cache.
   */
  const [
    searchArticles,
    setSearchArticles,
  ] = useState([]);

  const [
    searchPagination,
    setSearchPagination,
  ] = useState({
    page: 1,
    limit: PAGE_LIMIT,
    total: 0,
    totalPages: 1,
    hasMore: false,
  });

  const [
    searchLoading,
    setSearchLoading,
  ] = useState(false);

  const [
    searchLoadingMore,
    setSearchLoadingMore,
  ] = useState(false);

  const [
    searchError,
    setSearchError,
  ] = useState('');

  const [
    completedSearchQuery,
    setCompletedSearchQuery,
  ] = useState('');

  const searchRequestIdRef =
    useRef(0);

  const normalizedQuery = String(
    query || ''
  ).trim();

  const isSearching =
    normalizedQuery.length >= 2;

  /*
   * These aliases allow the existing JSX below
   * to render either cached Latest News or local
   * search results without being rewritten.
   */
  const articles = isSearching
    ? searchArticles
    : cachedArticles;

  const pagination = isSearching
    ? searchPagination
    : cachedPagination;

  const loading = isSearching
    ? searchLoading ||
      completedSearchQuery !==
        normalizedQuery
    : cachedLoading ||
      !cachedHasFetched;

  const loadingMore = isSearching
    ? searchLoadingMore
    : cachedLoadingMore;

  const error = isSearching
    ? searchError
    : cachedError;

  /*
   * First visit loads normally. Later visits use
   * the cache immediately and refresh silently
   * only when the cache is older than 60 seconds.
   */
  useEffect(() => {
    ensureNews();
  }, [ensureNews]);

  const fetchSearchArticles =
    useCallback(
      async ({
        page = 1,
        append = false,
        searchQuery,
      }) => {
        if (
          !searchQuery ||
          searchQuery.length < 2
        ) {
          return;
        }

        const requestId =
          searchRequestIdRef.current + 1;

        searchRequestIdRef.current =
          requestId;

        if (append) {
          setSearchLoadingMore(true);
        } else {
          setSearchLoading(true);
          setSearchError('');
        }

        try {
          const response = await api.get(
            '/news',
            {
              params: {
                page,
                limit: PAGE_LIMIT,
                q: searchQuery,
              },
            }
          );

          if (
            requestId !==
            searchRequestIdRef.current
          ) {
            return;
          }

          const nextArticles =
            Array.isArray(
              response.data?.articles
            )
              ? response.data.articles
              : [];

          const receivedPagination =
            response.data?.pagination || {
              page,
              limit: PAGE_LIMIT,
              total:
                nextArticles.length,
              totalPages: 1,
              hasMore: false,
            };

          setSearchArticles(
            (previousArticles) => {
              if (!append) {
                return nextArticles;
              }

              const existingIds =
                new Set(
                  previousArticles.map(
                    (article) =>
                      String(
                        article?._id ||
                          article?.canonicalUrl
                      )
                  )
                );

              const uniqueNewArticles =
                nextArticles.filter(
                  (article) => {
                    const identity =
                      String(
                        article?._id ||
                          article?.canonicalUrl
                      );

                    return (
                      identity &&
                      !existingIds.has(
                        identity
                      )
                    );
                  }
                );

              return [
                ...previousArticles,
                ...uniqueNewArticles,
              ];
            }
          );

          setSearchPagination({
            page:
              Number(
                receivedPagination.page
              ) || page,

            limit:
              Number(
                receivedPagination.limit
              ) || PAGE_LIMIT,

            total:
              Number.isFinite(
                Number(
                  receivedPagination.total
                )
              )
                ? Number(
                    receivedPagination.total
                  )
                : nextArticles.length,

            totalPages:
              Number(
                receivedPagination.totalPages
              ) || 1,

            hasMore:
              Boolean(
                receivedPagination.hasMore
              ),
          });

          setCompletedSearchQuery(
            searchQuery
          );

          setSearchError('');
        } catch (requestError) {
          if (
            requestId !==
            searchRequestIdRef.current
          ) {
            return;
          }

          console.error(
            '❌ Failed to search News:',
            requestError
          );

          const message =
            requestError?.response?.data
              ?.message ||
            'News could not be loaded right now.';

          if (!append) {
            setSearchArticles([]);
          }

          setCompletedSearchQuery(
            searchQuery
          );

          setSearchError(message);
        } finally {
          if (
            requestId ===
            searchRequestIdRef.current
          ) {
            setSearchLoading(false);
            setSearchLoadingMore(false);
          }
        }
      },
      []
    );

  /*
   * Debounce only applies to typed searches.
   * The normal Latest News list is handled by
   * NewsContext above.
   */
  useEffect(() => {
    if (!isSearching) {
      /*
       * Invalidate any search request that may
       * still be completing after the query was
       * cleared.
       */
      searchRequestIdRef.current += 1;

      setSearchArticles([]);
      setSearchPagination({
        page: 1,
        limit: PAGE_LIMIT,
        total: 0,
        totalPages: 1,
        hasMore: false,
      });

      setSearchLoading(false);
      setSearchLoadingMore(false);
      setSearchError('');
      setCompletedSearchQuery('');

      return;
    }

    setSearchError('');

    const timeoutId =
      window.setTimeout(() => {
        fetchSearchArticles({
          page: 1,
          append: false,
          searchQuery:
            normalizedQuery,
        });
      }, SEARCH_DEBOUNCE_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [
    normalizedQuery,
    isSearching,
    fetchSearchArticles,
  ]);

  const handleRetry = () => {
    if (isSearching) {
      fetchSearchArticles({
        page: 1,
        append: false,
        searchQuery:
          normalizedQuery,
      });

      return;
    }

    refreshNews();
  };

  const handleLoadMore = () => {
    if (
      loading ||
      loadingMore ||
      !pagination.hasMore
    ) {
      return;
    }

    if (isSearching) {
      fetchSearchArticles({
        page:
          searchPagination.page + 1,
        append: true,
        searchQuery:
          normalizedQuery,
      });

      return;
    }

    loadMoreNews();
  };



  const renderHero = () => {
    return (
      <header className="news-tab-hero">
        <div className="news-tab-hero-icon">
          <img
            src={piNetworkLogo}
            alt="Pi Network"
            className="news-tab-hero-logo"
          />
        </div>

        <div className="news-tab-hero-copy">
          <span className="news-tab-eyebrow">
            Verified Pi Network updates
          </span>

          <h1>
            What’s happening in Pi
          </h1>

          <p>
            Official ecosystem news,
            product announcements and
            important Pi Network updates
            in one place.
          </p>
        </div>
      </header>
    );
  };

  const renderSkeletons = () => {
    return (
      <div
        className="news-tab-list"
        aria-label="Loading news"
      >
        {[1, 2, 3].map((item) => (
          <div
            className="news-tab-skeleton"
            key={`news-skeleton-${item}`}
          >
            <div className="news-tab-skeleton-image" />

            <div className="news-tab-skeleton-body">
              <div className="news-tab-skeleton-meta" />

              <div className="news-tab-skeleton-title" />

              <div className="news-tab-skeleton-title news-tab-skeleton-title--short" />

              <div className="news-tab-skeleton-text" />

              <div className="news-tab-skeleton-text news-tab-skeleton-text--short" />
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (
    loading &&
    articles.length === 0
  ) {
    return (
      <section className="news-tab">
        {renderHero()}
        {renderSkeletons()}
      </section>
    );
  }

  return (
    <section className="news-tab">
      {renderHero()}

      <div className="news-tab-heading-row">
        <div>
          <span className="news-tab-heading-label">
            {normalizedQuery.length >= 2
              ? 'Search results'
              : 'Latest news'}
          </span>

          <p className="news-tab-heading-description">
            {normalizedQuery.length >= 2
              ? `Articles matching “${normalizedQuery}”`
              : 'Directly collected from the official Pi Network Blog.'}
          </p>
        </div>

        {!loading &&
          pagination.total > 0 && (
            <span className="news-tab-count">
              {pagination.total}
            </span>
          )}
      </div>

      {error &&
      articles.length === 0 ? (
        <div className="news-tab-state-card">
          <div className="news-tab-state-icon">
            !
          </div>

          <h2>
            News is temporarily
            unavailable
          </h2>

          <p>{error}</p>

          <button
            type="button"
            className="news-tab-retry-btn"
            onClick={handleRetry}
          >
            Try again
          </button>
        </div>
      ) : null}

      {!error &&
      !loading &&
      articles.length === 0 ? (
        <div className="news-tab-state-card">
          <div className="news-tab-state-icon news-tab-state-icon--logo">
            <img
              src={piNetworkLogo}
              alt=""
            />
          </div>

          <h2>
            {normalizedQuery.length >= 2
              ? 'No matching news found'
              : 'No news available yet'}
          </h2>

          <p>
            {normalizedQuery.length >= 2
              ? 'Try another keyword or clear the search field.'
              : 'Official Pi Network updates will appear here when available.'}
          </p>
        </div>
      ) : null}

      {articles.length > 0 && (
        <div className="news-tab-list">
          {articles.map((article) => (
            <NewsArticleCard
              key={
                article._id ||
                article.canonicalUrl
              }
              article={article}
            />
          ))}
        </div>
      )}

      {error &&
        articles.length > 0 && (
          <div className="news-tab-inline-error">
            <span>{error}</span>

            <button
              type="button"
              onClick={handleLoadMore}
            >
              Retry
            </button>
          </div>
        )}

      {pagination.hasMore &&
        articles.length > 0 && (
          <button
            type="button"
            className="news-tab-load-more"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore
              ? 'Loading more…'
              : 'Load more news'}
          </button>
        )}

      {!pagination.hasMore &&
        articles.length > 0 && (
          <p className="news-tab-end-note">
            You’re all caught up.
          </p>
        )}
    </section>
  );
}

export default NewsTab;
